import { formatHoldCleaningDate } from "../../../hold-cleaning-intelligence/components/HoldCleaningUi";
import type { HoldCleaningRunAiUsage } from "../../../hold-cleaning-intelligence/models/hold-cleaning.models";

const stageLabels = {
  classification: "Email classification",
  extraction: "Email / attachment extraction",
  image_category: "Image categorization",
};

const HoldCleaningRunCost = ({ usage }: { usage: HoldCleaningRunAiUsage }) => (
  <section className="border-current/10 mt-4 rounded-lg border p-3" aria-label="Latest run AI usage">
    <h3 className="text-sm font-semibold">Latest run AI usage</h3>
    <p className="mt-1 text-xs opacity-75">
      {usage.requestType} · {formatHoldCleaningDate(usage.startedAt)} ·{" "}
      {usage.imageCategorizationRunning ? "Categorizing images — cost still updating" : usage.runStatus}
    </p>
    <p className="mt-1 break-all font-mono text-xs">Run {usage.runId}</p>
    <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
      <div>
        <p className="text-xs opacity-75">Estimated AI cost this run</p>
        <p className="text-lg font-semibold">${usage.estimatedCostUsd.toFixed(4)}</p>
      </div>
      <div>
        <p className="text-xs opacity-75">Tracked calls this run</p>
        <p className="text-lg font-semibold">{usage.invocationCount.toLocaleString()}</p>
      </div>
      <div>
        <p className="text-xs opacity-75">Calls with unknown usage this run</p>
        <p className="text-lg font-semibold">{usage.unreportedInvocationCount.toLocaleString()}</p>
      </div>
    </div>
    <p className="mt-2 text-xs opacity-75">
      Reported AI usage only, included in the cumulative total below—not an additional charge. Excludes worker, storage
      and other infrastructure costs.
    </p>
    {usage.unreportedInvocationCount > 0 && (
      <p className="mt-2 text-xs font-semibold">
        Some calls are in flight or have no reported usage. Their cost is unknown, not zero.
      </p>
    )}
    {usage.invocationCount === 0 && (
      <p className="mt-2 text-xs">No AI calls recorded for this run. Older untracked calls cannot be estimated.</p>
    )}
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer">Cost and tokens by processing step</summary>
      <p className="mt-2">
        Reported tokens: {usage.promptTokens.toLocaleString()} input | {usage.completionTokens.toLocaleString()} output{" "}
        | {usage.totalTokens.toLocaleString()} total. Pricing: {usage.pricingVersions.join(", ") || "No calls yet"}.
      </p>
      <ul className="mt-2 space-y-1">
        {usage.stages.map((stage) => (
          <li key={stage.stage}>
            {stageLabels[stage.stage]}: ${stage.estimatedCostUsd.toFixed(4)} · {stage.invocationCount.toLocaleString()}{" "}
            calls · {stage.totalTokens.toLocaleString()} reported tokens · {stage.unreportedInvocationCount} with
            unknown usage
          </li>
        ))}
      </ul>
    </details>
  </section>
);

export default HoldCleaningRunCost;
